import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import { SortingState } from "@tanstack/react-table";
import { AnalyticsCards } from "Common/Components/AnalyticsCards/AnalyticsCards";
import ActionButton from "Common/Components/Buttons/ActionButton";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import StatusBadge from "Common/Components/StatusBadge/StatusBadge";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import Colors from "Constants/Color";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import usePermission from "hooks/usePermission";
import { ClipboardList, FileText } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Card, Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { createSelector } from "reselect";
import {
  exportPurchaseRequestCSV,
  fetchPRAnalytics,
  fetchPRs,
} from "slices/Procurement/purchase-request/thunk";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import StatusChip from "pages/Enquiry/Components/StatusChip";
import Chip from "components/Chips/Chip";
import { hideTagsSearchListingSection } from "../../utils/tagsSearchEmpty";

interface PurchaseRequestProps {
  noWrapper?: boolean;
}

const PurchaseRequest: React.FC<PurchaseRequestProps> = ({ noWrapper = false }) => {
  document.title = "Purchse Request List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { tags: tagsParam } = useParams<{ tags?: string }>();

  const selectPRs = createSelector(
    (state: any) => state.PRReducer,
    (pr) => ({
      prList: pr.prList,
      rowCount: pr.rowCount,
      loading: pr.loading,
      error: pr.error,
      analyticsData: pr.analyticsData,
      loadingAnalytics: pr.loadingAnalytics,
    }),
  );

  const { prList, rowCount, loading, error, analyticsData } =
    useSelector(selectPRs);
  const [tableInstance, setTableInstance] = useState<any>();

  const { canView, canCreate, canUpdate, permissions } = usePermission(
    app_modules?.purchase_management?.app_name,
    app_modules?.purchase_management?.modules?.purchase_request,
  );

  console.log(permissions);

  const tableState = usePreservedTableState({
    key: TableKeys.PURCHASE_MANAGEMENT.PR,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>();
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const tagFromUrl = useMemo(() => {
    if (!tagsParam) return "";
    try {
      return decodeURIComponent(tagsParam).trim();
    } catch {
      return tagsParam.trim();
    }
  }, [tagsParam]);
  const effectiveSearch = tagFromUrl || search;

  useEffect(() => {
    dispatch(
      fetchPRs({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search: effectiveSearch,
        ...(tagFromUrl ? { type: "tag" } : {}),
        filters: filterValue,
      }),
    );
  }, [dispatch, pageIndex, pageSize, sorting, effectiveSearch, filterValue]);

  useEffect(() => {
    setData(prList);
  }, [prList]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const trimmedInput = searchInput.trim();

      const dateMatch = trimmedInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const isoDate = `${year}-${month}-${day}`;
        setSearch(isoDate);
      } else {
        setSearch(trimmedInput);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    if (!tagFromUrl) return;
    setSearchInput(tagFromUrl);
    setSearch(tagFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagFromUrl]);

  useEffect(() => {
    dispatch(fetchPRAnalytics());
  }, [dispatch]);

  const statuses = useMemo(
    () => [
      {
        label: "Total Purchase Requests",
        icon: <FileText size={18} />,
        bgColor: "bg-blue-50",
        textColor: "text-blue-600",
        count: analyticsData?.total_count || 0,
        // subLabel: "Total purchase requests",
      },
      {
        label: "Approved Purchase Requests",
        icon: <ClipboardList size={18} />,
        bgColor: "bg-red-50",
        textColor: "text-red-600",
        count: analyticsData?.approved_count || 0,
        // subLabel: "Approved purchase requests",
      },
      {
        label: "Pending Purchase Requests",
        icon: <FileText size={18} />,
        bgColor: "bg-yellow-50",
        textColor: "text-yellow-600",
        count: analyticsData?.pending_count || 0,
        // subLabel: "Pending purchase requests",
      },
      {
        label: "PO Created",
        icon: <FileText size={18} />,
        bgColor: "bg-green-50",
        textColor: "text-green-600",
        count: analyticsData?.po_created_count || 0,
        // subLabel: "PO created",
      },
    ],
    [analyticsData],
  );

  const DynamicAction =
    canView || canUpdate
      ? [
          {
            header: "Action",
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }: { row: any }) => {
              const status: string = row.original.status?.toLowerCase() || "";
              const isApproved = status?.toLowerCase() === "approved";
              const isRejected = status?.toLowerCase() === "rejected";
              const isPending = status?.toLowerCase() === "pending";
              const po_created =
                status?.toLowerCase() === "po created" ||
                status?.toLowerCase() === "partial po created";

              const canApprove = permissions?.PRApproval || false;

              const menuItems = [
                ...(!po_created && !isApproved
                  ? [
                      {
                        icon: EditIcon,
                        label: "Edit Purchase Request",
                        permission: canUpdate,
                        onClick: (pr: any) => {
                          navigate(`/edit-purchase-request/${pr?.pr_id}`, {
                            state: pr,
                          });
                        },
                        color: "secondary",
                      },
                    ]
                  : []),
                ...(!po_created && canApprove
                  ? [
                      {
                        icon: CheckCircleIcon,
                        label: "Approve Purchase Request",
                        permission: canUpdate,
                        onClick: (pr: any) => {
                          navigate(
                            `/approve-purchase-request-form/${pr?.pr_id}`,
                          );
                        },
                        color: "success",
                      },
                    ]
                  : []),
                {
                  icon: InfoIcon,
                  label: "View Purchase Request Details",
                  permission: canView,
                  onClick: (pr: any) => {
                    navigate(`/tags-search/purchase-request-view-details/${pr?.pr_id}`);
                  },
                  color: "primary",
                },
              ];
              return (
                <DynamicMenuItem row={row.original} menuItems={menuItems} />
              );
            },
          },
        ]
      : [];

  const columns = useMemo(
    () => [
      // {
      //   header: "Sr No.",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      // },
      {
        header: "PR No.",
        accessorKey: "pr_no",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_REQUEST?.FILTER_DROPDOWN(
          "pr_no",
        )}`,
        cell: ({ row }: { row: any }) => {
          return row.original.pr_no || "-";
        },
      },

      {
        header: "PR Date",
        accessorKey: "pr_date",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.pr_date} />
        ),
      },
      {
        header: "Initiated By",
        accessorKey: "initiated_by",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_REQUEST?.FILTER_DROPDOWN(
          "initiated_by",
        )}`,
        cell: ({ row }: { row: any }) => {
          return row.original.initiated_by || "-";
        },
      },
      {
        header: "PR Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_REQUEST?.FILTER_DROPDOWN(
          "status",
        )}`,
        cell: ({ row }: { row: any }) => {
          const status: string = row.original.status;
          return <StatusChip status={row.original?.status} />;

          // let backgroundColor = "#ddd";
          // if (status?.toLowerCase() === "pending") {
          //   backgroundColor = "#FDE4B4";
          // }
          // if (status?.toLowerCase() === "approved") {
          //   backgroundColor = "#B7FAEB";
          // }
          // if (status?.toLowerCase() === "po created") {
          //   backgroundColor ="#E5CCFF";
          // }

          // return (
          //   <StatusBadge
          //     status={status}
          //     bgColor={backgroundColor}
          //     color="#0e0c0c"
          //   />
          // );
        },
      },
      {
        header: "Reviewed By",
        accessorKey: "approved_by_user__username",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.PURCHASE_MANAGEMENT?.PURCHASE_REQUEST?.FILTER_DROPDOWN(
          "approved_by_user__username",
        )}`,
        cell: ({ row }: { row: any }) => {
          const u = row.original?.approved_by_user;
          if (!u) return "-";
          // const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
          return u.username || "-";
        },
      },
      {
        header: "Reviewed Date",
        accessorKey: "approved_date",
          enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.approved_date} />
        ),
      },
      // {
      //   header: "Attachments",
      //   accessorKey: "pr_document",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   filterType: "dropdown" as const,
      //   cell: ({ row }: { row: any }) => {
      //     return row.original.pr_document ? (
      //       <Link target="_blank" to={row.original.pr_document}>
      //         View Attachment
      //       </Link>
      //     ) : (
      //       "No Document"
      //     );
      //   },
      // },
      {
        header: "Notes",
        accessorKey: "notes",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const notes = row.original.notes;

          if (!notes) return "-";

          return (
            <Tooltip title={notes} arrow placement="top">
              <Box
                sx={{
                  maxWidth: 250,
                  display: "-webkit-box",
                  WebkitLineClamp: 4, // 👈 change to 3 if needed
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  cursor: "pointer",
                  lineHeight: "1.4em",
                  maxHeight: "5.6em", // 4 lines × lineHeight
                }}
              >
                {notes}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        header: "Tags",
        accessorKey: "tags",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const tags: string[] = row.original.tags || [];
          if (!tags.length) return "-";

          const maxVisible = 3;
          const visible = tags.slice(0, maxVisible);
          const remaining = tags.length - visible.length;
          const tooltipText = tags.join(", ");

          return (
            <Tooltip
              arrow
              placement="top"
              title={tooltipText}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "13px",
                    whiteSpace: "normal",
                  },
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                  minWidth: "150px",
                  cursor: "pointer",
                }}
              >
                {visible.map((tag: string) => (
                  <Chip key={tag}>{tag}</Chip>
                ))}
                {remaining > 0 && <Chip>+{remaining} more</Chip>}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.created_by_user;
          return (
            <NameWithDate
              name={user?.username}
              date={row.original.created_at}
            />
          );
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.updated_by_user;
          return (
            <NameWithDate
              name={user?.username}
              date={row.original.updated_at}
            />
          );
        },
      },

      ...DynamicAction,
    ],
    [navigate, canView, canUpdate],
  );

  const sectionHeader = (
    <>
      <Row className="align-items-center g-2 mb-4">
        <Col lg={3} className="me-auto">
          <h6 className="card-title mb-0">Purchase Request</h6>
        </Col>
  
      </Row>

    </>
  );

  const sectionTable = (
    <LoaderErrorWrapper loading={loading} error={error}>
      <MasterTableContainer
        columns={columns}
        data={data}
        totalRecords={rowCount}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        colValue={setFilterValue}
        sorting={sorting}
        onSortingChange={setSorting}
        manualSorting={true}
        isBordered={false}
        isPagination={true}
        isGlobalFilter={false}
        SearchPlaceholder="Search ..."
        onTableReady={setTableInstance}
      />
    </LoaderErrorWrapper>
  );

  if (
    hideTagsSearchListingSection(
      noWrapper,
      loading,
      rowCount,
      (data as any[])?.length ?? 0,
      error,
    )
  ) {
    return null;
  }

  if (noWrapper) {
    return (
      <div className="mb-4">
        {sectionHeader}
        <div className="mt-4">{sectionTable}</div>
      </div>
    );
  }

  return (
    <Container fluid className="page-content rounded-6">
      <Row className="mb-3">
        <AnalyticsCards statuses={statuses} />
      </Row>

      <Row id="listPage">
        <Row id="invoiceList">
          <Col lg={12}>
            <Card className="shadow-none">
              <Card.Header className="mb-3">{sectionHeader}</Card.Header>
              <Card.Body className="mt-3">{sectionTable}</Card.Body>
            </Card>
          </Col>
        </Row>
      </Row>
    </Container>
  );
};

export default PurchaseRequest;
